import React from 'react';
import { availableBalance } from './walletFigures';
import { resolvePayNow, shouldOfferTopup } from './payNow';
import './payNowPanel.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * THE ONE CHECKOUT BLOCK (2026-07-18).
 *
 * Replaces every three-way "Wallet / Card / Wallet+card" picker. The client sees
 * one summary that reconciles on screen and one toggle. The RAIL is derived
 * (see payNow.js) — never picked.
 *
 * THE THREE QUESTIONS (owner, 2026-07-19 — item 14): every surface hosting this
 * panel must answer, at a glance,
 *   1. what is my wallet balance NOW        → the toggle's "AED X available",
 *   2. how much will my CARD be charged     → the card row, rendered ALWAYS —
 *      when the wallet covers the whole amount it says so explicitly instead
 *      of leaving a bare 0.00,
 *   3. what is my wallet balance AFTER      → the after row, whenever the
 *      wallet contributes.
 * Those rows are the whole answer — no extra explanatory sentences.
 *
 * BALANCE MODEL (2026-07-19): the wallet figure here is
 * walletFigures.availableBalance() — total − reserved − payouts. Due price
 * adjustments never reduce it, and this panel renders no owed-money caveats:
 * "owed reduces what the wallet can pay here" is a false sentence.
 *
 * Deliberately renders no submit button: each surface owns its own primary
 * action wording and its own submit handler. This component owns the FIGURES
 * and the toggle, so the arithmetic can never drift between surfaces.
 *
 * Props:
 *   amount        number  — what is being paid.
 *   wallet        object  — the server wallet summary (may be null while loading).
 *   useWallet     bool    — the toggle, owned by the parent (see useWalletToggle).
 *   onUseWallet   fn      — toggle setter.
 *   onTopUp       fn|null — opens the add-funds flow; when null the nudge is hidden.
 *   label         string  — what the amount row is called ("Price adjustment", …).
 *   disabled      bool    — while submitting.
 *   spendable     number|null — OPTIONAL surface-specific ceiling, used instead of
 *                 walletFigures.availableBalance(wallet). Exists for ONE reason:
 *                 the pending-invoice rail settles Zoho invoices, which BONUS
 *                 credit may not pay, so its ceiling is
 *                 min(availableBalance, cash_balance) — exactly what the server
 *                 clamps to. That is a rule about bonus credit, never about
 *                 owed adjustments (which reduce no ceiling anywhere). Callers
 *                 with no narrower rule must omit this and get the canonical
 *                 figure.
 *   entityBreakdown array|null — OPTIONAL per-billing-entity split of `amount`
 *                 ([{label, amount}], multi-billing clients only). When present
 *                 the single amount row becomes one row per entity — display
 *                 only, the plan arithmetic still runs on `amount`. Null/absent
 *                 (every normal client) renders exactly as before.
 */
export default function PayNowPanel({
    amount, wallet, useWallet, onUseWallet, onTopUp,
    label = 'Amount', disabled = false, spendable: spendableOverride = null,
    entityBreakdown = null,
    // Q2's wording. Defaults are the card rail's, so every existing surface is
    // untouched; the bank-transfer rail passes its own because "to pay by card"
    // is simply false there (2026-08-14 — the wallet toggle belongs on both
    // payment options, and each must describe its own leg truthfully).
    // Order (2026-08-14, invoice payment page): the decision comes FIRST,
    // directly under the payment-method choice, and the figures below react to
    // it. Default false — every other surface keeps figures-then-toggle.
    toggleFirst = false,
    // Node rendered between the decision and the figures. The invoice page puts
    // its per-billing-profile summary here: the wallet toggle stays directly
    // under the payment-method cards, and the summary sits with the figures it
    // breaks down (2026-08-14).
    beforeRows = null,
    dueLabel = 'To pay by card',
    coveredLabel = 'No card charge — covered by your wallet',
}) {
    // The override engages ONLY when a caller passed an actual number.
    // Number(null) is 0 and Number.isFinite(0) is true, so the old
    // `Number.isFinite(Number(spendableOverride))` check treated the DEFAULT
    // (null) as an override of zero — every override-less surface computed its
    // whole plan against a 0 wallet, hid the toggle, and sent the full amount
    // to card. That is the owner's "a 350-AED wallet could contribute NOTHING
    // toward 304.50" screen.
    const hasOverride = spendableOverride != null && Number.isFinite(Number(spendableOverride));
    const ceiling = hasOverride
        ? Number(spendableOverride)
        : (wallet ? availableBalance(wallet) : 0);
    const plan = resolvePayNow({ amount, spendable: ceiling, useWallet });

    // Q2's answer when there is no card leg because the wallet covers it all.
    const walletCoversAll = !plan.needsCard && plan.walletPortion > 0.005;

    // Per-entity split (multi-billing clients only — null for everyone else).
    const entityRows = Array.isArray(entityBreakdown) && entityBreakdown.length > 0
        ? entityBreakdown
        : null;

    // The decision + its nudge, rendered either above or below the figures
    // (see toggleFirst) — one definition, so the two orders can never drift.
    const decisionBlock = (
        <>
            {plan.hasWalletMoney && (
                    <label className={`pnp__toggle ${disabled ? 'is-disabled' : ''}`}>
                        <span className="pnp__toggleText">
                            Use my wallet balance
                            <span className="pnp__toggleAmt">{money(ceiling)} available</span>
                        </span>
                        <input
                            type="checkbox"
                            role="switch"
                            className="pnp__toggleInput"
                            checked={useWallet}
                            onChange={(e) => onUseWallet(e.target.checked)}
                            disabled={disabled}
                        />
                    </label>
                )}
    
                {/* Quiet nudge, not a third option, and NOT a second button (owner,
                    2026-07-19: "it seems like wallet is the only option since a major
                    button — keep it only as a text with the arrow"). A bordered
                    full-width control here outranks the actual Pay button and makes
                    topping up look like the way to pay. It is a text link with an
                    arrow, opening with "Or" so it reads as the aside it is.
                    One wording for both cases: the empty-wallet variant used to lead
                    with "Earn bonuses", which pitched the wallet ahead of paying.
    
                    THE NUDGE STAYS (item 15, 2026-07-19). It was once deleted on a misreading of his
                    "that wallet balance calculation under the button" sentence —
                    he was locating the balance block, not ordering this link gone.
                    If you find this block missing, that is a DEFECT: restore it. */}
                {shouldOfferTopup(plan) && onTopUp && (
                    <button
                        type="button"
                        className="pnp__nudge"
                        onClick={onTopUp}
                        disabled={disabled}
                    >
                        Or top up your wallet and earn bonuses →
                    </button>
                )}
        </>
    );

    return (
        <div className="pnp">
            {toggleFirst ? decisionBlock : null}
            {beforeRows}
            <div className="pnp__rows">
                {entityRows ? (
                    // One row per billing entity in place of the flat amount row
                    // (mirrors the N-Genius paypage order summary). The wallet /
                    // card rows below still reconcile against the same total.
                    entityRows.map((e, i) => (
                        <div key={i} className="pnp__row">
                            <span>{e.label}</span>
                            <strong>{money(e.amount)}</strong>
                        </div>
                    ))
                ) : (
                    <div className="pnp__row">
                        <span>{label}</span>
                        <strong>{money(plan.amount)}</strong>
                    </div>
                )}

                {plan.walletPortion > 0.005 && (
                    <div className="pnp__row pnp__row--wallet">
                        <span>From your wallet</span>
                        <strong>−{money(plan.walletPortion)}</strong>
                    </div>
                )}

                {/* Q2 — RENDERED ALWAYS. "Will a card transaction be required?"
                    must have an answer on every surface: a figure when a card
                    leg exists, an explicit reassurance when the wallet covers
                    the whole amount. */}
                <div className="pnp__row pnp__row--due">
                    <span>{walletCoversAll ? coveredLabel : dueLabel}</span>
                    <strong>{money(plan.cardPortion)}</strong>
                </div>

                {/* NO "Wallet balance after" row (owner 2026-07-19, on the live
                    invoice + booking screens): the balance-after figure is not
                    wanted on the checkout breakdown. The toggle already states
                    the balance NOW; what leaves the wallet is the "From your
                    wallet" line. Do not reintroduce a balance-after row here. */}
            </div>

            {/* The single decision — and Q1's answer. Hidden entirely when there
                is nothing in the wallet to use — an unusable toggle is just
                another thing to read.

                B7 (2026-07-19): the input is still a
                checkbox — same semantics, same handler — but renders as a
                SWITCH (payNowPanel.css draws the pill + knob itself, so dark
                mode cannot restyle it invisible), laid out like mobile's
                PayNowPanel row: label + available on the left, switch on the
                right. role="switch" says what it now looks like. */}
            {toggleFirst ? null : decisionBlock}

            {/* NO secure-checkout note here (2026-07-19). The
                secure page speaks for itself; a sentence about it under every
                checkout is the repetitive text the owner keeps removing. */}

            {/* The owedNote that used to render here ("AED X owed in price
                adjustments reduces what the wallet can pay here") is DELETED,
                not moved: under the 2026-07-19 balance ruling that sentence is
                false — owed money reduces nothing. Owed adjustments surface on
                the Amount owed tile and the itemised screens, never as a
                checkout caveat. Do not reintroduce it. */}
        </div>
    );
}

/**
 * The toggle's default: ON whenever there is anything available. Exported so
 * every surface defaults identically, and re-evaluates when the balance loads.
 */
export const walletToggleDefault = (wallet) => availableBalance(wallet) > 0.005;
